import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import useRouter from './useRouter';
import SidebarHelper from 'general/components/Sidebars/SidebarHelper';
import useGetMenuItemsList from 'general/components/Sidebars/MenuItemsList';
import AppConstants from 'general/constants/AppConstants';
import AppRoute from 'general/constants/AppRoute';
import RoleUtils from 'general/utils/RoleUtils';

export const useRoutePermission = (user, agencyRoleList, permissionList) => {
    const router = useRouter();
    const config = useSelector((state) => state.app.appConfig?.config);
    const poaUserId = _.find(config, (item) => item.key === 'poaUserId')?.value;
    const menuItems = useGetMenuItemsList(user, poaUserId, agencyRoleList);

    const getAllMenuPaths = (formattedItems) => {
        const paths = [];
        formattedItems.forEach((item) => {
            if (item.type === "item" && item.path) paths.push(item.path);
            if (item.subMenuItems?.length > 0) {
                item.subMenuItems.forEach((subItem) => {
                    if (subItem.path) paths.push(subItem.path);
                });
            }
        });
        return [...new Set(paths)];
    };

    const isPermissionReady = useMemo(() => {
        if (_.isEmpty(user)) return false;
        return true;
    }, [user]);

    const formattedItems = useMemo(() => {
        if (!isPermissionReady) return [];
        return SidebarHelper.checkPermissionList(menuItems, permissionList);
    }, [menuItems, permissionList, isPermissionReady]);

    const allAllowedPaths = useMemo(() => {
        if (!isPermissionReady) return [];
        return getAllMenuPaths(formattedItems);
    }, [formattedItems, isPermissionReady]);

    const roleWhiteListPaths = useMemo(() => {
        if (!isPermissionReady) return [];
        if (RoleUtils.isAdminUiRole(user?.role)) {
            return AppConstants.userRoleRouteWhiteList?.[`${user?.role || ''}`.trim().toUpperCase()] || [];
        }

        const primaryAgencyRole = RoleUtils.getPrimaryAgencyRole(user, agencyRoleList);
        if (primaryAgencyRole) {
            return AppConstants.userRoleRouteWhiteList?.AGENCY?.[primaryAgencyRole] || [];
        }
        return AppConstants.userRoleRouteWhiteList?.[user?.role] || [];
    }, [isPermissionReady, user, agencyRoleList]);

    const normalizePath = (path) => {
        if (!path) return "/";
        return path.replace(/\/+$/, "") || "/";
    };

    const isOwnAgencyDetailPath = (currentPath) => {
        const primaryAgencyRole = RoleUtils.getPrimaryAgencyRole(user, agencyRoleList);
        const ownAgencyId = user?.agency?.agencyId || user?.agencyId || user?.agency?.id;
        if (!primaryAgencyRole || !ownAgencyId) return false;

        const ownPathByRole = {
            SALE: `${AppRoute.saleInfo}/${ownAgencyId}`,
            INVESTOR: `${AppRoute.investorInfo}/${ownAgencyId}`,
            GROUP: `${AppRoute.groupInfo}/${ownAgencyId}`,
            LANDLORD: `${AppRoute.landlordInfo}/${ownAgencyId}`,
        };
        const ownPath = normalizePath(ownPathByRole[primaryAgencyRole]);
        return currentPath === ownPath || currentPath.startsWith(`${ownPath}/`);
    };

    const hasPermission = useMemo(() => {
        if (!isPermissionReady) return true;
        if (RoleUtils.isAdminUiRole(user?.role)) return true;

        const currentPath = normalizePath(router.pathname);
        if (currentPath === "/") return true;
        if (isOwnAgencyDetailPath(currentPath)) return true;

        const isRoleWhiteListPath = roleWhiteListPaths.some((allowedPath) => {
            const normalizedAllowed = normalizePath(allowedPath);
            if (currentPath === normalizedAllowed) return true;
            if (normalizedAllowed !== "/" && currentPath.startsWith(normalizedAllowed + "/")) return true;
            return false;
        });
        if (isRoleWhiteListPath) return true;

        return allAllowedPaths.some((allowedPath) => {
            const normalizedAllowed = normalizePath(allowedPath);
            if (currentPath === normalizedAllowed) return true;
            if (normalizedAllowed !== "/" && currentPath.startsWith(normalizedAllowed + "/")) return true;
            return false;
        });
    }, [router.pathname, allAllowedPaths, roleWhiteListPaths, isPermissionReady, user, agencyRoleList]);

    return { hasPermission, isPermissionReady };
};
